import React, { useMemo } from 'react';
import { Box, CalendarDate, NewCalendar, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { ValuesMap } from '@/common/types';

import { EmployerCalendarPayload } from './EmployerCalendar.types';
import EmployerCalendarDayEvents from './EmployerCalendarDayEvents';

interface Props {
  datesMap?: ValuesMap<EmployerCalendarPayload[]>;
  currentMonth: CalendarDate;
  onChangeCurrentMonth: (value: CalendarDate) => void;
  selectedDate?: CalendarDate | null;
  onSelectDate: (value?: CalendarDate) => void;
}

const EmployerCalendar: React.FC<Props> = ({
  currentMonth,
  onChangeCurrentMonth,
  datesMap = {},
  onSelectDate,
  selectedDate,
}) => {
  const blockPreviousButton = useMemo(() => {
    const previousYear = dayjs().year() - 1;

    return currentMonth.add(-1, 'month').year() < previousYear;
  }, [currentMonth]);

  const blockNextButton = useMemo(() => {
    const nextYear = dayjs().year() + 1;

    return currentMonth.add(1, 'month').year() > nextYear;
  }, [currentMonth]);
  return (
    <NewCalendar
      data-testid="employer-calendar"
      monthDayObject={currentMonth}
      onChangeMonthDayObject={onChangeCurrentMonth}
      round="container1Round"
      border={{ color: 'border2', size: '1px' }}
    >
      {({ days }) => (
        <>
          <NewCalendar.Header>
            <Box direction="row" fill>
              <Text data-testid="employer-calendar_month-title" size="2xl" weight={700}>
                {currentMonth.format('MMMM YYYY')}
              </Text>
            </Box>
            <div>
              <Box direction="row" gap="spacing8">
                <NewCalendar.Header.PreviousMonthButton
                  disabled={blockPreviousButton}
                  data-testid="employer-calendar_prev-month"
                />
                <NewCalendar.Header.NextMonthButton
                  disabled={blockNextButton}
                  data-testid="employer-calendar_next-month"
                />
              </Box>
            </div>
          </NewCalendar.Header>
          <NewCalendar.WeekDays />
          <NewCalendar.Days>
            {days.map((date) => (
              <NewCalendar.Days.Item
                key={date.toISOString()}
                item={date}
                isSelected={selectedDate?.isSame(date, 'day')}
                onClick={onSelectDate}
                data-testid={`employer-calendar_day-${date.format('YYYY-MM-DD')}`}
              >
                <EmployerCalendarDayEvents dates={datesMap[date.format(DEFAULT_DATE_FORMAT)]} />
              </NewCalendar.Days.Item>
            ))}
          </NewCalendar.Days>
        </>
      )}
    </NewCalendar>
  );
};
export default EmployerCalendar;
